import { useContext, useEffect, useRef, useState } from 'react';
import ysFixWebmDuration from 'fix-webm-duration';
import { MicIcon, RecordIcon, StopRecording, TrashIcon } from 'assets/icons';
import { Button, ButtonSize, ButtonStyle } from 'common/button';
import {
  AlbumAddContentTypes,
  AlbumContentType,
  NotificationType,
} from 'common/enums';
import { ContentEntryTitle } from 'common/album-detail-modal/components/content-entry-title';
import { maxTimeClipRecordedInSeconds } from 'config/constants';
import { appActions, AppContext } from 'context/app-context';
import { SelfRecordingContext } from 'context/self-recording-context';
import { selfRecordingActions } from 'context/self-recording-context/action-types';
import {
  VaultDetailContext,
  vaultDetailActions,
} from 'context/vault-detail-context';
import { directUploadFile } from 'helpers/direct-upload';
import { classnames, formatRecordingTime } from 'helpers/utils';
import { NotificationObject } from 'models/notificationObject';
import { VaultsController } from 'networking/controllers/vaults-controller';
import { publishMemoryAdded } from 'common/returning-onboarding/onboarding-status';
import styles from './record-entry.module.scss';

interface RecordEntryProps {
  kind: AlbumAddContentTypes;
  vaultId: number;
  albumId: number;
  // Closes the modal. The upload + memory creation continue in the background,
  // shown as a loading placeholder card in the album grid.
  onDone: () => void;
}

type Phase = 'idle' | 'recording' | 'preview';
// The microphone (and camera) are opened when the recorder opens, so they've
// warmed up before Record is pressed. Some devices (Bluetooth headsets, some
// USB mics) deliver nothing for a second or two after opening; starting the
// recorder in the same click lost the first words, or the whole take.
type MicState = 'opening' | 'ready' | 'silent' | 'error';

// A mic that's on always has some noise; exact digital silence means no audio
// is reaching us yet.
const SIGNAL_FLOOR = 0.0005;
// How long to wait for audio before warning that the mic seems silent.
const MIC_WAIT_MS = 4000;
// Smaller than this is just the file header: nothing was recorded.
const MIN_TAKE_BYTES = 1024;
// Hand recorded data over every second, so a take that's cut short keeps what
// was captured.
const RECORDER_TIMESLICE_MS = 1000;

// Modal recorder for new audio/video memories: record → preview the take →
// Delete (re-record) or Save. Save closes the modal immediately and creates
// the memory in the background (same pattern as the file-upload flow).
export const RecordEntry = ({
  kind,
  vaultId,
  albumId,
  onDone,
}: RecordEntryProps) => {
  const isAudio = kind === AlbumAddContentTypes.audio;
  const {
    dispatch,
    state: { entryMeta },
  } = useContext(SelfRecordingContext);
  const { dispatch: appDispatch } = useContext(AppContext);
  const { dispatch: vaultDetailDispatch } = useContext(VaultDetailContext);

  const [phase, setPhase] = useState<Phase>('idle');
  const [timer, setTimer] = useState(0);
  const [title, setTitle] = useState('');
  const [take, setTake] = useState<{ file: File; url: string } | null>(null);
  const [micState, setMicState] = useState<MicState>('opening');
  const [stream, setStream] = useState<MediaStream | null>(null);

  const streamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const meterFrameRef = useRef(0);
  const levelRef = useRef<HTMLSpanElement>(null);
  // Audio has arrived since the devices were opened / since recording began.
  const micLiveRef = useRef(false);
  const heardAudioRef = useRef(false);
  const mountedRef = useRef(false);
  const openAttemptRef = useRef(0);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startedAtRef = useRef(0);
  const mimeTypeRef = useRef('');
  const liveVideoRef = useRef<HTMLVideoElement>(null);
  const takeRef = useRef<{ url: string } | null>(null);
  takeRef.current = take;

  const notifyError = (errorTitle: string, message?: string) =>
    appDispatch({
      type: appActions.NOTIFICATION,
      notification: new NotificationObject({
        show: true,
        title: errorTitle,
        message,
        type: NotificationType.Error,
      }),
    });

  const closeDevices = () => {
    cancelAnimationFrame(meterFrameRef.current);
    audioContextRef.current?.close().catch(() => {});
    audioContextRef.current = null;
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    micLiveRef.current = false;
    setStream(null);
  };

  // Watches the microphone's input: drives the level ring and tells when audio
  // is actually arriving.
  const startMeter = (mediaStream: MediaStream) => {
    const [audioTrack] = mediaStream.getAudioTracks();
    const AudioContextClass =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext })
        .webkitAudioContext;
    const context = new AudioContextClass();
    context.resume().catch(() => {});
    audioContextRef.current = context;
    const analyser = context.createAnalyser();
    analyser.fftSize = 1024;
    context.createMediaStreamSource(mediaStream).connect(analyser);
    const samples = new Float32Array(analyser.fftSize);
    const openedAt = performance.now();

    const markLive = () => {
      heardAudioRef.current = true;
      if (!micLiveRef.current) {
        micLiveRef.current = true;
        setMicState('ready');
      }
    };

    const tick = () => {
      let peak = 0;
      if (context.state === 'running') {
        analyser.getFloatTimeDomainData(samples);
        samples.forEach((sample) => {
          peak = Math.max(peak, Math.abs(sample));
        });
        if (peak > SIGNAL_FLOOR && !audioTrack?.muted) markLive();
      } else if (audioTrack && !audioTrack.muted) {
        // The browser won't run audio analysis here: trust the track.
        markLive();
      }
      if (!micLiveRef.current && performance.now() - openedAt > MIC_WAIT_MS) {
        setMicState((state) => (state === 'opening' ? 'silent' : state));
      }
      levelRef.current?.style.setProperty(
        '--level',
        String(Math.min(1, peak * 4)),
      );
      meterFrameRef.current = requestAnimationFrame(tick);
    };
    tick();
  };

  const openDevices = async () => {
    openAttemptRef.current += 1;
    const attempt = openAttemptRef.current;
    setMicState('opening');
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: !isAudio,
      });
      // Closed, or reopened, while the browser was asking for access.
      if (!mountedRef.current || attempt !== openAttemptRef.current) {
        mediaStream.getTracks().forEach((track) => track.stop());
        return;
      }
      streamRef.current = mediaStream;
      setStream(mediaStream);
      startMeter(mediaStream);
    } catch (error) {
      if (mountedRef.current) setMicState('error');
    }
  };

  // Open the devices with the recorder; release them, and the preview's blob
  // URL, when it closes.
  useEffect(() => {
    mountedRef.current = true;
    openDevices();
    return () => {
      mountedRef.current = false;
      closeDevices();
      if (takeRef.current) URL.revokeObjectURL(takeRef.current.url);
    };
  }, []);

  // Live camera preview, before and during the recording.
  useEffect(() => {
    if (liveVideoRef.current) liveVideoRef.current.srcObject = stream;
  }, [stream, phase]);

  const pickRecorderOptions = () => {
    let options: { mimeType: string; videoBitsPerSecond?: number } = {
      mimeType: 'video/webm',
    };
    if (isAudio) {
      if (MediaRecorder.isTypeSupported('audio/webm; codecs=opus')) {
        options = { mimeType: 'audio/webm; codecs=opus' };
      } else if (MediaRecorder.isTypeSupported('audio/webm')) {
        options = { mimeType: 'audio/webm' };
      } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
        options = { mimeType: 'audio/mp4' };
      }
    } else if (MediaRecorder.isTypeSupported('video/webm; codecs=vp9')) {
      options = { mimeType: 'video/webm; codecs=vp9' };
    } else if (MediaRecorder.isTypeSupported('video/webm')) {
      options = { mimeType: 'video/webm' };
    } else if (MediaRecorder.isTypeSupported('video/mp4')) {
      options = { mimeType: 'video/mp4', videoBitsPerSecond: 100000 };
    }
    mimeTypeRef.current = options.mimeType;
    return options;
  };

  const startRecording = () => {
    const mediaStream = streamRef.current;
    if (!mediaStream) return;
    try {
      const recorder = new MediaRecorder(mediaStream, pickRecorderOptions());
      chunksRef.current = [];
      heardAudioRef.current = false;
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunksRef.current.push(event.data);
      };
      recorder.onstop = async () => {
        // The recorder was closed mid-take: nothing to keep or reopen.
        if (!mountedRef.current) return;
        const raw = new Blob(chunksRef.current, {
          type: mimeTypeRef.current,
        });
        const capturedAudio =
          raw.size >= MIN_TAKE_BYTES && heardAudioRef.current;
        closeDevices();
        if (!capturedAudio) {
          notifyError(
            isAudio
              ? 'We didn’t capture any audio'
              : 'We didn’t capture any sound',
            'Check that your microphone is connected, selected and not muted, then try again.',
          );
          setTimer(0);
          setPhase('idle');
          openDevices();
          return;
        }
        const fixed = await ysFixWebmDuration(
          raw,
          Date.now() - startedAtRef.current,
          { logger: false },
        );
        // Strip codec params ("; codecs=...") from the file's content type:
        // ActiveStorage's disk service 422s direct-upload PUTs whose declared
        // content type has parameters (request.content_mime_type is bare).
        // MediaRecorder still uses the full mime type above to pick the codec.
        const file = new File(
          [fixed],
          isAudio ? 'audio-recording' : 'video-recording',
          { type: mimeTypeRef.current.split(';')[0].trim() },
        );
        setTake({ file, url: URL.createObjectURL(file) });
        setPhase('preview');
      };
      recorder.start(RECORDER_TIMESLICE_MS);
      recorderRef.current = recorder;
      startedAtRef.current = Date.now();
      setTimer(0);
      setPhase('recording');
    } catch (error) {
      notifyError(
        isAudio
          ? 'Something went wrong with the microphone'
          : 'Something went wrong with the camera or microphone',
        'Please check your permissions and try again.',
      );
    }
  };

  const stopRecording = () => recorderRef.current?.stop();

  useEffect(() => {
    if (phase !== 'recording') return undefined;
    const interval = setInterval(() => setTimer((t) => t + 1), 1000);
    return () => clearInterval(interval);
  }, [phase]);

  useEffect(() => {
    if (phase === 'recording' && timer >= maxTimeClipRecordedInSeconds) {
      stopRecording();
    }
  }, [timer, phase]);

  const discardTake = () => {
    if (take) URL.revokeObjectURL(take.url);
    setTake(null);
    setTimer(0);
    setPhase('idle');
    openDevices();
  };

  // Save: close the modal right away; the S3 upload + memory creation continue
  // in the background and drive the album grid's placeholder card via context.
  const saveTake = () => {
    if (!take) return;
    const name =
      title.trim() || (isAudio ? 'Audio recording' : 'Video recording');
    const upload = {
      type: isAudio ? AlbumContentType.audio : AlbumContentType.video,
      name,
      data: name,
      uploadId: `record-${Date.now()}`,
    };
    const { file } = take;
    // Read before the modal closes: the Add Entry dialog clears it.
    const meta = entryMeta;
    onDone();
    (async () => {
      dispatch({
        type: selfRecordingActions.addUpload,
        addUpload: { ...upload, isUploading: true },
      });
      try {
        const uploaded = await directUploadFile(file);
        await VaultsController.createDocument(vaultId, {
          clip: {
            videoSignedId: uploaded.signedId,
            name,
            recordedAt: new Date(Date.now()),
            albumId,
            ...meta,
          },
        });
        dispatch({
          type: selfRecordingActions.updateUpload,
          updateUpload: { ...upload, isUploading: false, error: false },
        });
        // Recorded from the top-bar "New" modal, so no album screen is around
        // to patch its list — re-read the vault instead.
        vaultDetailDispatch({
          type: vaultDetailActions.REQUEST_ALBUMS_REFRESH,
        });
        publishMemoryAdded();
        appDispatch({
          type: appActions.NOTIFICATION,
          notification: new NotificationObject({
            show: true,
            title: `${name} saved`,
            type: NotificationType.Success,
          }),
        });
      } catch (error) {
        dispatch({
          type: selfRecordingActions.updateUpload,
          updateUpload: { ...upload, isUploading: false, error: true },
        });
        notifyError(`${name} could not be saved`, 'Please try again');
      }
    })();
  };

  const renderStage = () => {
    if (phase === 'preview' && take) {
      return isAudio ? (
        <div className={classnames(styles.stage, styles.audioStage)}>
          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <audio controls src={take.url} className={styles.audioPreview} />
        </div>
      ) : (
        <div className={styles.stage}>
          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <video controls src={take.url} className={styles.videoPreview} />
        </div>
      );
    }
    if (isAudio) {
      return (
        <div className={classnames(styles.stage, styles.audioStage)}>
          <span className={styles.micBadgeWrap}>
            {/* Grows with the input level, so you can see the mic hears you. */}
            <span ref={levelRef} className={styles.micLevel} />
            <span
              className={classnames(styles.micBadge, {
                [styles.micRecording]: phase === 'recording',
              })}
            >
              <MicIcon />
            </span>
          </span>
        </div>
      );
    }
    return (
      <div className={styles.stage}>
        {stream ? (
          // eslint-disable-next-line jsx-a11y/media-has-caption
          <video
            ref={liveVideoRef}
            autoPlay
            muted
            playsInline
            className={styles.videoPreview}
          />
        ) : (
          <span className={styles.videoHint}>
            {micState === 'error'
              ? 'Camera unavailable'
              : 'Your camera preview will appear here'}
          </span>
        )}
      </div>
    );
  };

  return (
    <div className={styles.recordEntry}>
      <ContentEntryTitle title={title} isCreatingNew onTitleChange={setTitle} />

      {renderStage()}

      {phase === 'idle' && micState !== 'ready' && (
        <p
          className={classnames(styles.micStatus, {
            [styles.micStatusWarning]: micState !== 'opening',
          })}
          role="status"
        >
          {micState === 'opening' && 'Preparing your microphone…'}
          {micState === 'silent' &&
            'We can’t hear your microphone. Check that it’s connected, selected and not muted.'}
          {micState === 'error' &&
            `${isAudio ? 'Microphone' : 'Camera or microphone'} access is blocked. Allow it in your browser, then try again.`}
        </p>
      )}

      <div className={styles.controls}>
        <span className={styles.timer}>
          {formatRecordingTime(timer)}
          <span className={styles.timerMax}>
            {' / '}
            {formatRecordingTime(maxTimeClipRecordedInSeconds)}
          </span>
        </span>

        <div className={styles.actions}>
          {phase === 'idle' && micState === 'error' && (
            <Button
              buttonSize={ButtonSize.Medium}
              buttonStyle={ButtonStyle.SecondaryGray}
              onClick={openDevices}
            >
              Try again
            </Button>
          )}
          {phase === 'idle' && micState !== 'error' && (
            <Button
              buttonSize={ButtonSize.Medium}
              buttonStyle={ButtonStyle.Primary}
              // A mic that seems silent may still work: let them try.
              disabled={micState === 'opening'}
              onClick={startRecording}
            >
              <span className={styles.buttonContent}>
                <RecordIcon />
                Start Recording
              </span>
            </Button>
          )}
          {phase === 'recording' && (
            <Button
              buttonSize={ButtonSize.Medium}
              buttonStyle={ButtonStyle.DestructivePrimary}
              onClick={stopRecording}
            >
              <span className={styles.buttonContent}>
                <StopRecording />
                Stop Recording
              </span>
            </Button>
          )}
          {phase === 'preview' && (
            <>
              <Button
                buttonSize={ButtonSize.Medium}
                buttonStyle={ButtonStyle.SecondaryGray}
                onClick={discardTake}
              >
                <span className={styles.buttonContent}>
                  <TrashIcon />
                  Delete
                </span>
              </Button>
              <Button
                buttonSize={ButtonSize.Medium}
                buttonStyle={ButtonStyle.Primary}
                onClick={saveTake}
              >
                Save
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
